import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineSearch } from "react-icons/ai";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { LineWave } from "react-loader-spinner";
import { toast } from "react-toastify";
import "react-quill/dist/quill.snow.css";
import Confirmation from "../../../components/confirmation/Confirmation";
import "./FeatureDropsList.css";
import {
  deleteUpcomingFeature,
  getAllUpcomingFeatures,
  setCurrentFeature,
} from "../../../redux/slices/upcomingFeatureSlice";

const statusBadgeClass = (isActive) =>
  isActive
    ? "bg-[#0F7D53] text-[#9AF6CA]"
    : "bg-[#4B4963] text-[#B8B7CB]";

const toPlainText = (value = "") => value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export default function FeatureDropsList() {
  const dispatch = useDispatch();
  const { features, isLoading, isSaving } = useSelector(
    (state) => state.upcomingFeatureReducer,
  );
  const [searchText, setSearchText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(getAllUpcomingFeatures());
  }, [dispatch]);

  const filteredFeatureDrops = useMemo(() => {
    const value = searchText.trim().toLowerCase();
    if (!value) return features;
    return features.filter((item) => {
      const title = (item.title || item.name || "").toLowerCase();
      const description = toPlainText(item.description || item.systemMessage || "").toLowerCase();
      return title.includes(value) || description.includes(value);
    });
  }, [features, searchText]);
  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    dispatch(deleteUpcomingFeature(deleteTarget)).then((response) => {
      if (deleteUpcomingFeature.fulfilled.match(response)) {
        toast.success("System message deleted successfully");
      } else {
        toast.error(
          response?.payload?.response?.data?.message ||
            response?.payload?.message ||
            "Failed to delete system message",
        );
      }
      setDeleteTarget(null);
    });
  };

  return (
    <div className="text-white">
      <h1 className="text-left text-white text-3xl font-bold ml-1 mb-4">
        In System Messages
      </h1>

      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="relative rounded-full bg-[#372F5B] w-full max-w-[620px]">
          <AiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8F8AAE]" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search"
            className="bg-[#372F5B] h-10 px-4 py-2 pl-10 text-white rounded-full text-sm focus:outline-none w-full"
          />
        </div>

        <Link
          to="/panel/add-feature-drop"
          className="bg-[#E74B5A] hover:bg-[#F15B67] text-white font-semibold py-2 px-4 rounded-md inline-block"
        >
          Add System Message
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        {filteredFeatureDrops.length === 0 ? (
          <div className="bg-[#271F4A] border border-[#3A315F] rounded-md px-4 py-8 text-center text-[#A4A0BE] md:col-span-2">
            No system messages found
          </div>
        ) : (
          filteredFeatureDrops.map((item) => (
            <div
              key={item.id}
              className="bg-[#271F4A] border border-[#3A315F] rounded-md p-4 flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-left text-[#ECEBFA] font-semibold text-lg leading-tight">
                  {item.title || item.name || "Untitled"}
                  
                </h3>
                <div className="flex items-center gap-3 text-base">
                <span
                  className={`inline-flex items-center justify-center text-xs px-3 py-1 rounded-full ${statusBadgeClass(item.isActive)}`}
                >
                  {item.isActive ? "Active" : "Inactive"}
                </span>
                  <Link
                    to={`/panel/edit-feature-drop/${item.id}`}
                    className="text-[#5AA7FF] hover:text-[#87C0FF]"
                    title="Edit"
                    onClick={() => dispatch(setCurrentFeature(item))}
                  >
                    <FiEdit2 />
                  </Link>
                  <button
                    type="button"
                    className="text-[#FF5C6A] hover:text-[#FF818A]"
                    title="Delete"
                    onClick={() => setDeleteTarget(item.id)}
                  >
                    <MdDeleteOutline />
                  </button>
                </div>
              </div>

              <div
                className="feature-drop-html ql-editor text-left text-[#ECEBFA] bg-[#211849] rounded-md break-words"
                dangerouslySetInnerHTML={{
                  __html: item.description || item.systemMessage || "<p>-</p>",
                }}
              />
            </div>
          ))
        )}
      </div>

      {deleteTarget && (
        <Confirmation
          message="delete this system message"
          buttonText="Delete"
          confirmDelete={handleDelete}
          cancelDelete={() => setDeleteTarget(null)}
        />
      )}

      {(isLoading || isSaving) && (
        <div className="loader">
          <LineWave color={"#fff"} loading={isLoading || isSaving} size={150} />
        </div>
      )}
    </div>
  );
}
